import { useEffect, useState } from "react";
import {
  Area,
  AreaChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import "./GitHubLineGraph.css";

const GitHubLineGraph = ({ username = "konti-aditi" }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [filter, setFilter] = useState("Week");
  const [pullRequests, setPullRequests] = useState([]);
  const [showAllPRs, setShowAllPRs] = useState(false);

  const token = process.env.REACT_APP_GITHUB_PAT;
  const currentYear = new Date().getFullYear();
  const availableYears = Array.from({ length: 5 }, (_, i) => currentYear - i);

  const getWeekNumber = (date) => {
    const firstJan = new Date(date.getFullYear(), 0, 1);
    const days = Math.floor((date - firstJan) / (24 * 60 * 60 * 1000));
    return Math.ceil((date.getDay() + 1 + days) / 7);
  };

  const fetchContributions = async () => {
    setLoading(true);
    try {
      if (filter === "Year") {
        const allYearData = [];
        for (let year of availableYears) {
          const query = `
            query {
              user(login: "${username}") {
                contributionsCollection(from: "${year}-01-01T00:00:00Z", to: "${year}-12-31T23:59:59Z") {
                  contributionCalendar {
                    weeks {
                      contributionDays {
                        contributionCount
                      }
                    }
                  }
                }
              }
            }
          `;

          const res = await fetch("https://api.github.com/graphql", {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ query }),
          });

          const json = await res.json();
          const weeks =
            json?.data?.user?.contributionsCollection?.contributionCalendar?.weeks || [];

          const flatDays = weeks.flatMap((week) => week.contributionDays);
          const yearlyTotal = flatDays.reduce(
            (acc, day) => acc + day.contributionCount,
            0
          );

          allYearData.push({
            label: `${year}`,
            contributions: yearlyTotal,
          });
        }
        setData(allYearData.reverse());
      } else {
        const query = `
          query {
            user(login: "${username}") {
              contributionsCollection(from: "${selectedYear}-01-01T00:00:00Z", to: "${selectedYear}-12-31T23:59:59Z") {
                contributionCalendar {
                  weeks {
                    contributionDays {
                      date
                      contributionCount
                    }
                  }
                }
              }
            }
          }
        `;

        const res = await fetch("https://api.github.com/graphql", {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ query }),
        });

        const json = await res.json();
        const weeks =
          json?.data?.user?.contributionsCollection?.contributionCalendar?.weeks || [];

        const flatDays = weeks.flatMap((week) => week.contributionDays);

        let formatted = [];
        if (filter === "Day") {
          formatted = flatDays.map(({ date, contributionCount }) => ({
            label: new Date(date).toLocaleDateString(),
            contributions: contributionCount,
          }));
        } else if (filter === "Week") {
          const weeklyMap = {};
          flatDays.forEach(({ date, contributionCount }) => {
            const week = `W${getWeekNumber(new Date(date))}`;
            weeklyMap[week] = (weeklyMap[week] || 0) + contributionCount;
          });
          formatted = Object.entries(weeklyMap).map(([label, count]) => ({
            label,
            contributions: count,
          }));
        } else if (filter === "Month") {
          const monthlyMap = {};
          flatDays.forEach(({ date, contributionCount }) => {
            const month = new Date(date).toLocaleString("default", {
              month: "short",
            });
            monthlyMap[month] = (monthlyMap[month] || 0) + contributionCount;
          });
          formatted = Object.entries(monthlyMap).map(([label, count]) => ({
            label,
            contributions: count,
          }));
        }

        setData(formatted);
      }
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPullRequests = async () => {
    try {
      const query = `
        query {
          user(login: "${username}") {
            pullRequests(first: 100, orderBy: {field: CREATED_AT, direction: DESC}) {
              nodes {
                title
                url
                createdAt
                repository {
                  nameWithOwner
                }
                state
              }
            }
          }
        }
      `;

      const res = await fetch("https://api.github.com/graphql", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ query }),
      });

      const json = await res.json();
      const prs = json?.data?.user?.pullRequests?.nodes || [];
      setPullRequests(prs);
    } catch (err) {
      console.error("Error fetching pull requests:", err);
    }
  };

  useEffect(() => {
    fetchContributions();
  }, [selectedYear, filter]);

  useEffect(() => {
    fetchPullRequests();
  }, []);

  return (
    <div className="github-line-graph-container">
      <h2 className="title">GITHUB CONTRIBUTIONS GRAPH</h2>

      <div className="filter-bar">
        {["Day", "Week", "Month", "Year"].map((f) => (
          <button
            key={f}
            className={`filter-button ${filter === f ? "active" : ""}`}
            onClick={() => setFilter(f)}
          >
            {f}
          </button>
        ))}

        {filter !== "Year" && (
          <select
            className="year-selector"
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
          >
            {availableYears.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        )}
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : data.length === 0 ? (
        <p>No contributions found.</p>
      ) : (
        <div className="chart-scroll-container">
          <div className="chart-inner-wrapper">
            <AreaChart
              width={Math.min(1200, Math.max(600, data.length * 60))}
              height={400}
              data={data}
              margin={{ top: 20, right: 30, left: 10, bottom: 20 }}
            >
              <defs>
                <linearGradient id="colorContributions" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#828282" stopOpacity={0.5} />
                  <stop offset="95%" stopColor="#828282" stopOpacity={0} />
                </linearGradient>
              </defs>

              <XAxis dataKey="label" tick={{ fontSize: 12 }} />
              <YAxis />
              <Tooltip />
              <Area
                type="monotone"
                dataKey="contributions"
                stroke="#828282"
                fillOpacity={1}
                fill="url(#colorContributions)"
              />
            </AreaChart>
          </div>
        </div>
      )}

      <div className="pull-requests-section">
        <div className="pull-requests-header">
          <h3>PULL REQUESTS</h3>
          {pullRequests.length > 3 && (
            <button
              className="toggle-pr-button"
              onClick={() => setShowAllPRs((prev) => !prev)}
            >
              {showAllPRs ? "Show Less" : "Show More"}
            </button>
          )}
        </div>
        {pullRequests.length === 0 ? (
          <p>No pull requests found.</p>
        ) : (
          <>
            <ul className="pr-list">
              {(showAllPRs ? pullRequests : pullRequests.slice(0, 3)).map((pr) => (
                <li key={pr.url} className="pr-item">
                  <a href={pr.url} target="_blank" rel="noopener noreferrer">
                    <strong>{pr.title}</strong>
                  </a>{" "}
                  <span className="pr-meta">
                    in <code>{pr.repository.nameWithOwner}</code> •{" "}
                    {new Date(pr.createdAt).toLocaleDateString()} • {pr.state}
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  );
};

export default GitHubLineGraph;
